import { doc, getDoc, updateDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { FILM_TIERS, SUBSCRIPTION_STATUS } from '../filmSubscriptionTiers';

/**
 * Subscription Service
 * Handles Film subscription operations with Firebase
 */

/**
 * Get user's Film subscription details
 * @param {string} userId - User ID
 * @returns {Promise<Object>} Subscription data
 */
export const getUserFilmSubscription = async (userId) => {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    
    if (!userDoc.exists()) {
      throw new Error('User not found');
    }
    
    const userData = userDoc.data();
    
    return {
      tier: userData.filmSubscriptionTier || FILM_TIERS.FREE,
      status: userData.filmSubscriptionStatus || SUBSCRIPTION_STATUS.ACTIVE,
      expiresAt: userData.filmSubscriptionExpiresAt,
      startedAt: userData.filmSubscriptionStartedAt,
      companyId: userData.enterpriseCompanyId || userData.filmCompanyId || null,
      isEnterpriseAdmin: userData.isEnterpriseAdmin || userData.isFilmEnterpriseAdmin || false
    };
  } catch (error) {
    console.error('Error fetching user subscription:', error);
    throw error;
  }
};

/**
 * Update user's Film subscription tier
 * @param {string} userId - User ID
 * @param {string} tier - New tier
 * @param {Object} options - Additional options
 * @returns {Promise<void>}
 */
export const updateUserFilmTier = async (userId, tier, options = {}) => {
  try {
    const updateData = {
      filmSubscriptionTier: tier,
      filmSubscriptionStatus: options.status || SUBSCRIPTION_STATUS.ACTIVE,
      filmSubscriptionExpiresAt: options.expiresAt || null,
      filmSubscriptionStartedAt: options.startedAt || new Date()
    };
    
    // Add Stripe IDs if provided
    if (options.stripeCustomerId) {
      updateData.stripeCustomerId = options.stripeCustomerId;
    }
    
    if (options.stripeSubscriptionId) {
      updateData.stripeSubscriptionId = options.stripeSubscriptionId;
    }
    
    await updateDoc(doc(db, 'users', userId), updateData);
    
    console.log(`✅ Updated user ${userId} to tier ${tier}`);
  } catch (error) {
    console.error('Error updating user tier:', error);
    throw error;
  }
};

/**
 * Cancel user's Film subscription
 * @param {string} userId - User ID
 * @returns {Promise<void>}
 */
export const cancelUserFilmSubscription = async (userId) => {
  try {
    await updateDoc(doc(db, 'users', userId), {
      filmSubscriptionStatus: SUBSCRIPTION_STATUS.CANCELLED
    });
    
    console.log(`✅ Cancelled subscription for user ${userId}`);
  } catch (error) {
    console.error('Error cancelling subscription:', error);
    throw error;
  }
};

/**
 * Grant trial access to user
 * @param {string} userId - User ID
 * @param {string} tier - Trial tier
 * @param {number} days - Trial duration in days
 * @returns {Promise<void>}
 */
export const grantTrialAccess = async (userId, tier, days = 14) => {
  try {
    const expiresAt = new Date();
    expiresAt.setDate(expiresAt.getDate() + days);
    
    await updateDoc(doc(db, 'users', userId), {
      filmSubscriptionTier: tier,
      filmSubscriptionStatus: SUBSCRIPTION_STATUS.TRIAL,
      filmSubscriptionExpiresAt: expiresAt,
      filmSubscriptionStartedAt: new Date()
    });
    
    console.log(`✅ Granted ${days}-day trial of ${tier} to user ${userId}`);
  } catch (error) {
    console.error('Error granting trial access:', error);
    throw error;
  }
};

/**
 * Get all users with Film subscriptions
 * @param {Object} filters - Filter options
 * @returns {Promise<Array>} List of users
 */
export const getAllFilmSubscribers = async (filters = {}) => {
  try {
    const baseConstraints = [];

    if (filters.tier) {
      baseConstraints.push(where('filmSubscriptionTier', '==', filters.tier));
    }

    if (filters.status) {
      baseConstraints.push(where('filmSubscriptionStatus', '==', filters.status));
    }

    const usersMap = new Map();
    const usersCollection = collection(db, 'users');

    const executeQuery = async (additionalConstraint) => {
      const snapshot = await getDocs(query(usersCollection, ...baseConstraints, additionalConstraint));
      snapshot.forEach((docSnap) => {
        if (!usersMap.has(docSnap.id)) {
          usersMap.set(docSnap.id, {
            id: docSnap.id,
            ...docSnap.data(),
          });
        }
      });
    };

    if (filters.companyId) {
      await executeQuery(where('enterpriseCompanyId', '==', filters.companyId));
      await executeQuery(where('filmCompanyId', '==', filters.companyId));
    } else {
      const snapshot = await getDocs(query(usersCollection, ...baseConstraints));
      snapshot.forEach((docSnap) => {
        usersMap.set(docSnap.id, {
          id: docSnap.id,
          ...docSnap.data(),
        });
      });
    }

    return Array.from(usersMap.values());
  } catch (error) {
    console.error('Error fetching film subscribers:', error);
    throw error;
  }
};

const subscriptionService = {
  getUserFilmSubscription,
  updateUserFilmTier,
  cancelUserFilmSubscription,
  grantTrialAccess,
  getAllFilmSubscribers
};

export default subscriptionService;

